import { ErrorModal, useOpenModal } from '@modusign/design-system';
import { ErrorType } from '@modusign/openapi-client-user/custom-client';
import { useEffect } from 'react';
import { MutationOptions, useMutation, useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';
import { push } from 'redux-first-history';

import { appServiceUrl } from '@libs/serviceUrls';

import useOpenPurgeDurationGuideModal from '@app/features/authentication/hooks/useOpenPurgeDurationGuideModal';
import redirect from '@app/libs/embedded/redirect';
import lastLoginTypeService from '@app/libs/lastLoginTypeService';
import { login } from '@app/libs/login';
import { InactivePageState } from '@app/routes/Authentication/Inactive';
import { loadAccount } from '@app/store/account/actions';
import { signInFailure, signInSuccess } from '@app/store/account/local/actions';
import { checkPasswordChange } from '@app/store/account/password/actions';
import { setUserEmail } from '@app/store/account/sign-up/actions';
import { modalOpen } from '@app/store/modal/actions';

interface SignInError extends Error {
  data: {
    blockUntil?: string;
    leavedAt?: string;
  };
}

export function isSignInError(error: Error | undefined): error is SignInError {
  return Boolean((error as SignInError | undefined)?.data);
}

export const getSignInMutationKey = () => ['signIn'] as const;

export default function useSignIn() {
  const client = useQueryClient();
  const dispatch = useDispatch();
  const openModal = useOpenModal();

  const openPurgeDurationGuideModal = useOpenPurgeDurationGuideModal();

  useEffect(() => {
    client.setMutationDefaults(getSignInMutationKey(), {
      mutationFn: ({ email, password }) => login({ type: 'email', email, password }),
      onSuccess: () => {
        lastLoginTypeService.removeLastLoginType();
        [signInSuccess(), loadAccount(), checkPasswordChange()].forEach(dispatch);
      },
      onError: (error, { email }) => {
        dispatch(signInFailure(error));
        dispatch(setUserEmail(email));

        const { data: maybeErrorInstance } = error.response ?? {};
        switch (error.message) {
          case INVALID_PASSWORD:
            dispatch(modalOpen('LOGIN_FAILURE_MODAL', { title: '로그인 실패' }));
            break;
          case EMAIL_NOT_AUTHENTICATED:
            dispatch(push('/authentication/signupcheck'));
            break;
          case USER_IS_INACTIVATED:
            dispatch(
              push<InactivePageState>('/authentication/inactive', {
                provider: 'local',
              })
            );
            break;
          case ONLY_ALLOWED_SSO:
            dispatch(
              modalOpen('CONFIRM', {
                title: '로그인 안내',
                content:
                  'SSO(통합 인증)로만 로그인할 수 있는 이용자입니다. \n SSO로 로그인해 주세요.',
                cancelText: '닫기',
                closeText: 'SSO로 로그인',
                onClose: () => redirect(`${appServiceUrl}/authentication/sso/signin`),
              })
            );
            break;
          case USER_BLOCKED:
            if (isSignInError(maybeErrorInstance) && maybeErrorInstance.data.blockUntil) {
              dispatch(
                modalOpen('LOGIN_FAILURE_BLOCK_MODAL', {
                  title: '로그인 제한 안내',
                  error,
                })
              );
            } else {
              dispatch(modalOpen('ACCOUNT_SUSPENDED_MODAL'));
            }
            break;
          default:
            if (isSignInError(maybeErrorInstance) && maybeErrorInstance.data.leavedAt) {
              openPurgeDurationGuideModal(maybeErrorInstance.data.leavedAt);
            } else {
              openModal((props) => (
                <ErrorModal
                  {...props}
                  error={error}
                  errorMessageMapper={SIGN_IN_ERRORS}
                  title="로그인 안내"
                />
              ));
            }
            break;
        }
      },
    } satisfies MutationOptions<void, ErrorType<Error>, { email: string; password: string }>);
  }, [dispatch, openModal, openPurgeDurationGuideModal, client]);

  return useMutation<void, ErrorType<Error>, { email: string; password: string }>({
    mutationKey: getSignInMutationKey(),
  });
}

const INVALID_PASSWORD = 'Invalid password';
const EMAIL_NOT_AUTHENTICATED = 'email not authenticated';
const USER_IS_INACTIVATED = 'User is inactivated';
const ONLY_ALLOWED_SSO = 'This user is only allowed to log in through SSO';
const USER_BLOCKED = 'User blocked';
const SIGN_IN_ERRORS: Record<string, string> = {
  'email user not found':
    '아래 소셜 계정으로 가입했거나,\n아직 가입하지 않은 이메일입니다.\n 확인 후, 다시 시도해 주세요.',
  'Left user': '이미 탈퇴한 사용자는 로그인할 수 없습니다.',
  'Not email user':
    '이메일 주소로 가입한 사용자가 아닙니다. 아래 해당하는 소셜 버튼을 눌러 로그인해 주세요.',
  'Max login attempts exceeded': '로그인 시도 횟수가 초과되었습니다.',
};
